import { NsKey } from "enums/NsKey";
import {
  formatSubsidiaryAmount,
  formatSubsidiaryCount,
  formatSubsidiaryDate
} from "features/new-actor/components/subsidiaries/subsidiaryFormatters";
import { SubsidiaryFormValues } from "features/new-actor/types/optim/subsidiaryFormValues";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { PenIcon, TrashIcon } from "components/icons/Icons";
import { Button } from "components/ui/Button";
import { Check } from "lucide-react";
import { FC } from "react";
import { formatSiret } from "shared/utils/formatSiret";
import { LP_BORDER, LP_TEXT, LP_TYPO } from "./legalProtectionTheme";

interface LegalProtectionSubsidiaryCardProps {
  subsidiary: SubsidiaryFormValues;
  /** Without `onEdit` / `onDelete` the matching button is not rendered: read-only card. */
  onEdit?: (subsidiary: SubsidiaryFormValues) => void;
  onDelete?: (id: string) => void;
}

/**
 * A subsidiary of a legal protection folder, in two zones: who it is on top, what it weighs
 * below the rule. The figures are the saved ones, formatted by the subsidiaries formatters.
 */
export const LegalProtectionSubsidiaryCard: FC<LegalProtectionSubsidiaryCardProps> = ({
  subsidiary,
  onEdit,
  onDelete
}) => {
  const { t } = useDynamicTranslation([NsKey.Common]);

  const renderFigure = (label: string, value: string) => (
    <div className="flex flex-col gap-0.5">
      <span className={`${LP_TYPO.microLabel} ${LP_TEXT.secondary}`}>{label}</span>
      <span className={`${LP_TYPO.cardFigure} ${LP_TEXT.title}`}>{value}</span>
    </div>
  );

  return (
    <div className={`relative flex h-full flex-col gap-4 rounded-xl border bg-white p-6 pb-4 ${LP_BORDER.card}`}>
      {/* Same edit and delete buttons as the borrower cards (BorrowerInsuredCard). */}
      {onEdit && (
        <div className="absolute right-4 top-4">
          <Button
            color="slate"
            round
            onClick={() => onEdit(subsidiary)}
            aria-label={t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SUBSIDIARY.EDIT", { name: subsidiary.companyName })}
          >
            <PenIcon className="text-black" />
          </Button>
        </div>
      )}

      <div className="flex flex-col items-start gap-2 pr-12">
        <p className={`${LP_TYPO.subsidiaryName} ${LP_TEXT.title}`}>{subsidiary.companyName}</p>
        <p className={`text-[13px] tabular-nums ${LP_TEXT.secondary}`}>
          {t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.LABELS.REGISTRATION_NUMBER")}
          {' '}
          {formatSiret(subsidiary.registrationNumber)}
        </p>
        {subsidiary.legalPersonId && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-[12px] font-bold text-primary">
            <Check className="h-3 w-3 flex-none" aria-hidden="true" />
            {t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SUBSIDIARY.EXISTING")}
          </span>
        )}
      </div>

      <div className={`mt-auto grid grid-cols-3 gap-3 border-t pt-4 ${LP_BORDER.rule}`}>
        {renderFigure(
          t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SUBSIDIARY.REVENUE"),
          formatSubsidiaryAmount(subsidiary.revenue)
        )}
        {renderFigure(
          t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SUBSIDIARY.EMPLOYEES"),
          formatSubsidiaryCount(subsidiary.employeesCount)
        )}
        {renderFigure(
          t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SUBSIDIARY.FISCAL_YEAR_END"),
          formatSubsidiaryDate(subsidiary.fiscalYearEnd)
        )}
      </div>

      {onDelete && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => onDelete(subsidiary.id)}
            aria-label={t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SUBSIDIARY.DELETE", { name: subsidiary.companyName })}
            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};
